import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { DateHandler } from '../scripts/date';
import { StaticDataStoreItem } from '../scripts/staticData';
import { ApiService } from './api.service';
import { LogRocketService } from './log-rocket.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public staticUserData = new StaticDataStoreItem('', '', this.apiService, { webServiceName: '' });
  public readySubject$ = new BehaviorSubject(false);
  public authorizedSubject$ = new BehaviorSubject(false);
  public employeeId = '';
  public unauthorizedErrorMessage = //this is the default error message, but ideally it will never be seen since when certain things happen more specific messages are shown
    'Your employee number is either invalid in SAP, or you are not setup to approve any cost centers in Davey Time.';

  constructor(
    private readonly apiService: ApiService,
    private readonly logRocket: LogRocketService,
  ) {
    this.apiService.getSelf$().subscribe({
      next: (state) => {
        this.setStaticData(state.EmployeeNum);
      },
      error: (error) => {
        this.handleUnauthorized('There was an error getting your authorization info for Davey Time approvals!');
        console.error('Issue getting employee information\n', error);
      },
    });
  }

  public getName(): string {
    try {
      //We assume these are strings but they are just made up properties with no type info, but this should work unless the data is bad
      return `${this.staticUserData.value.FirstName as string} ${this.staticUserData.value.LastName as string}`;
    } catch (error) {
      console.error('Unable to format name from static user data!', error);
      return '';
    }
  }

  public getEmployeeNumber(): string {
    //TODO: if this fails, we should get it from the auth object.
    try {
      return this.staticUserData.value.EmpId;
    } catch (e) {
      console.error('Employee number is not available', e);
      return 'Not available';
    }
  }

  public syncToCache(): void {
    this.staticUserData.updateLocalStorage();
  }

  public getCcInfo() {
    return {
      CostCenter: this.staticUserData.value.CostCenter as string,
      DESCRIPTION: '',
    };
  }

  public getWorkScheduleRule(): string {
    return this.staticUserData.value.WorkScheduleRule;
  }

  public getPayType(): string {
    return this.staticUserData.value.TimeMngtText;
  }

  public getCompanyCode(): string {
    return this.staticUserData.value.CompanyCode;
  }

  public getDefaultActivity(): string {
    return this.staticUserData.value.Default;
  }

  public signOut(): void {
    this.apiService.signOut$().subscribe();
  }

  private handleUnauthorized(errorMsg: string, employeeId = 'None Provided'): void {
    this.unauthorizedErrorMessage = errorMsg;
    this.employeeId = employeeId;
    this.authorizedSubject$.next(false);
    this.readySubject$.next(true);
  }

  private setStaticData(empId: string): void {
    this.staticUserData = new StaticDataStoreItem(empId, 'object', this.apiService, { webServiceName: '' });
    const localEmployee = this.staticUserData.tryLocal();
    if (localEmployee === false) {
      this.apiService.getOneEmployee$('123456', new DateHandler().getSapDateString(), empId, empId).subscribe({
        next: (x) => {
          this.staticUserData.value = x;
          this.staticUserData.updateLocalStorage();
          this.logRocket.identify(this.getEmployeeNumber(), this.getName(), this.getCcInfo().CostCenter);
          this.authorizedSubject$.next(true);
          this.readySubject$.next(true);
        },
        error: (error) => {
          this.handleUnauthorized('Your account is not properly set up', empId);
          console.error('Issue getting employee information to enter app, user may need to be set up\n', error);
        },
      });
    } else {
      this.staticUserData.value = localEmployee;
      this.authorizedSubject$.next(true);
      this.readySubject$.next(true);
    }
  }
}
